import type { NodeViewRendererProps } from '@tiptap/core'
import { Extension, InputRule, mergeAttributes, Node, nodeInputRule } from '@tiptap/core'
import { PluginKey } from '@tiptap/pm/state'
import Suggestion from '@tiptap/suggestion'
import { getMacroSuggestionPopup } from '../components/MacroSuggestionPopup'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    redmineMacroInline: {
      insertMacro: (macro: string) => ReturnType
    }
    redmineMacroBlock: {
      insertBlockMacro: (args: { title?: string }) => ReturnType
    }
  }
}

// Covers: {{toc}}, {{>toc}}, {{child_pages}}, {{include(...)}}, {{thumbnail(...)}},
//         {{issue(N)}}, {{macro_list}}, and any other {{name}} or {{name(args)}} form.
const INLINE_RE = /^\{\{(>?toc|child_pages|macro_list|include\([^)\n]*\)|thumbnail\([^)\n]*\)|issue\(\d+\)|[a-z_]+(?:\([^)\n]*\))?)\}\}/
const BLOCK_RE = /^\{\{collapse(?:\(([^)\n]*)\))?\n([\s\S]*?)\n\}\}/

export const RedmineMacroInline = Node.create({
  name: 'redmineMacroInline',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: true,
  defining: true,

  addAttributes() {
    return {
      macro: { default: '' },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-redmine-macro]',
        getAttrs: el => ({
          macro: el.getAttribute('data-redmine-macro'),
        }),
      },
    ]
  },

  renderHTML({ node }) {
    return [
      'span',
      mergeAttributes({
        'data-redmine-macro': node.attrs.macro,
        'class': 'redmine-macro-pill',
        'contenteditable': 'false',
      }),
      `{{${node.attrs.macro}}}`,
    ]
  },

  addCommands() {
    return {
      insertMacro: macro => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: { macro },
        })
      },
    }
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: /(\{\{(>?toc|child_pages|macro_list|include\([^)\n]*\)|thumbnail\([^)\n]*\)|issue\(\d+\))\}\})$/,
        type: this.type,
        getAttributes: match => ({ macro: match[2] }),
      }),
    ]
  },

  markdownTokenName: 'redmineMacroInline',

  markdownTokenizer: {
    name: 'redmineMacroInline',
    level: 'inline',
    start: src => src.indexOf('{{'),
    tokenize: (src) => {
      const m = INLINE_RE.exec(src)
      if (!m)
        return
      return {
        type: 'redmineMacroInline',
        raw: m[0],
        macro: m[1],
        tokens: [],
      } as any
    },
  },

  parseMarkdown(token) {
    return {
      type: 'redmineMacroInline',
      attrs: { macro: (token as any).macro ?? '' },
    }
  },

  renderMarkdown(node) {
    return `{{${node.attrs!.macro}}}`
  },
})

export const RedmineMacroBlock = Node.create({
  name: 'redmineMacroBlock',
  group: 'block',
  content: 'block+',
  selectable: true,
  draggable: true,
  isolating: true,

  addAttributes() {
    return {
      open: { default: false },
      title: { default: '' },
    }
  },

  parseHTML() {
    return [{
      tag: 'div[data-redmine-collapse]',
      getAttrs: el => ({ title: el.dataset.title ?? '' }),
    }]
  },

  renderHTML({ node }) {
    return [
      'div',
      mergeAttributes({
        'data-redmine-collapse': '',
        'data-title': node.attrs.title ?? '',
        'class': 'redmine-collapse',
      }),
      0,
    ]
  },

  addNodeView() {
    return ({ node, getPos, editor }: NodeViewRendererProps) => {
      const dom = document.createElement('div')
      dom.className = 'redmine-collapse'
      if (node.attrs.open)
        dom.classList.add('is-open')

      const header = document.createElement('div')
      header.className = 'redmine-collapse-header'
      header.contentEditable = 'false'

      const setAttr = (update: (a: Record<string, unknown>) => Record<string, unknown>) => {
        if (typeof getPos !== 'function')
          return
        editor.commands.command(({ tr }: { tr: any }) => {
          const pos = getPos()!
          const n = tr.doc.nodeAt(pos)
          if (n)
            tr.setNodeMarkup(pos, undefined, { ...n.attrs, ...update(n.attrs) })
          return true
        })
      }

      const toggle = document.createElement('button')
      toggle.type = 'button'
      toggle.className = 'icon icon-collapsed collapsible redmine-collapse-toggle'
      toggle.innerHTML = '<svg class="s18 icon-svg" aria-hidden="true"><use href="/assets/icons-a9735328.svg#icon--angle-down"></use></svg>'
      toggle.addEventListener('mousedown', (e) => {
        e.preventDefault()
        setAttr(a => ({ open: !a.open }))
      })

      const titleInput = document.createElement('input')
      titleInput.type = 'text'
      titleInput.className = 'redmine-collapse-title'
      titleInput.setAttribute('data-collapse-title', '')
      titleInput.placeholder = 'Title (optional)'
      titleInput.value = (node.attrs.title as string) ?? ''
      titleInput.addEventListener('input', () => setAttr(() => ({ title: titleInput.value })))
      titleInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter')
          e.preventDefault()
      })

      header.append(toggle, titleInput)

      const content = document.createElement('div')
      content.className = 'redmine-collapse-content'

      dom.append(header, content)

      return {
        dom,
        contentDOM: content,
        update(updatedNode) {
          if (updatedNode.type.name !== 'redmineMacroBlock')
            return false
          const isOpen = updatedNode.attrs.open as boolean
          dom.classList.toggle('is-open', isOpen)
          toggle.classList.toggle('icon-expanded', isOpen)
          toggle.classList.toggle('icon-collapsed', !isOpen)
          if (document.activeElement !== titleInput) {
            titleInput.value = (updatedNode.attrs.title as string) ?? ''
          }
          return true
        },
        stopEvent(event) {
          return header.contains(event.target as unknown as globalThis.Node)
        },
      }
    }
  },

  addInputRules() {
    return [
      new InputRule({
        find: /\{\{collapse(?:\(([^)\n]*)\))?\}\}$/,
        handler: ({ range, match, chain }) => {
          chain().deleteRange(range).insertBlockMacro({ title: match[1] ?? '' }).run()
        },
      }),
    ]
  },

  addCommands() {
    return {
      insertBlockMacro: ({ title = '' }) => ({ commands }) => {
        return commands.insertContent({
          type: this.name,
          attrs: { title },
          content: [{ type: 'paragraph' }],
        })
      },
    }
  },

  markdownTokenName: 'redmineMacroBlock',
  markdownTokenizer: {
    name: 'redmineMacroBlock',
    level: 'block',
    start: (src) => {
      const i = src.indexOf('{{')
      return i < 0 ? -1 : i
    },
    tokenize: (src, _tokens, lexer) => {
      const m = BLOCK_RE.exec(src)
      if (!m)
        return
      return {
        type: 'redmineMacroBlock',
        raw: m[0],
        title: m[1] ?? '',
        tokens: lexer.blockTokens(m[2]),
      } as any
    },
  },

  parseMarkdown(token, helpers) {
    const t = token as any
    return helpers.createNode('redmineMacroBlock', { title: t.title ?? '' }, helpers.parseChildren(t.tokens ?? []))
  },

  renderMarkdown(node, helpers) {
    const title = node.attrs?.title ?? ''
    const body = node.content ? helpers.renderChildren(node.content) : ''
    const opening = title ? `{{collapse(${title})` : `{{collapse`
    return `${opening}\n${body.trimEnd()}\n}}`
  },
})

/* ── Macro suggestion (triggers on {{) ─────────────────────────────── */

export interface MacroSuggestionItem {
  id: string
  label: string
  description: string
  insert: string // full text to insert, e.g. "toc" or "include(PageName)"
}

const BUILT_IN_MACROS: MacroSuggestionItem[] = [
  { id: 'toc', label: '{{toc}}', description: 'Table of contents', insert: 'toc' },
  { id: 'toc-right', label: '{{>toc}}', description: 'Right-aligned table of contents', insert: '>toc' },
  { id: 'child_pages', label: '{{child_pages}}', description: 'List of child pages', insert: 'child_pages' },
  { id: 'macro_list', label: '{{macro_list}}', description: 'List all available macros', insert: 'macro_list' },
  { id: 'include', label: '{{include(Page)}}', description: 'Include another wiki page', insert: 'include(Page)' },
  { id: 'collapse', label: '{{collapse(title)}}', description: 'Collapsible section', insert: 'collapse(title)' },
  { id: 'thumbnail', label: '{{thumbnail(img)}}', description: 'Image thumbnail', insert: 'thumbnail(img.png)' },
  { id: 'issue', label: '{{issue(N)}}', description: 'Display issue details', insert: 'issue(1)' },
]

export const MacroSuggestion = Extension.create({
  name: 'macroSuggestion',

  addProseMirrorPlugins() {
    return [
      Suggestion({
        pluginKey: new PluginKey('macroSuggestion'),
        editor: this.editor,
        char: '{',
        allowSpaces: false,
        // Only activate after '{{' (two consecutive braces)
        allow: ({ state, range }) => {
          const textBefore = state.doc.textBetween(
            Math.max(0, range.from - 2),
            range.from,
            '\0',
            '\0',
          )
          return textBefore.endsWith('{')
        },

        items: ({ query }) => {
          const q = query.toLowerCase()
          return Promise.resolve(
            q
              ? BUILT_IN_MACROS.filter(m => m.label.toLowerCase().includes(q) || m.description.toLowerCase().includes(q))
              : BUILT_IN_MACROS,
          )
        },

        command: ({ editor, range, props }) => {
          const item = props as MacroSuggestionItem
          const from = range.from - 1 // include the leading '{' before the trigger char
          if (item.id === 'collapse') {
            editor.chain().focus().deleteRange({ from, to: range.to }).insertBlockMacro({ title: 'title' }).run()
          }
          else {
            editor.chain().focus().deleteRange({ from, to: range.to }).insertContent({ type: 'redmineMacroInline', attrs: { macro: item.insert } }).run()
          }
        },

        render: () => ({
          onStart(props) {
            getMacroSuggestionPopup()?.show(props.items, props.clientRect!, props.command)
          },
          onUpdate(props) {
            getMacroSuggestionPopup()?.update(props.items, props.clientRect!)
          },
          onKeyDown({ event }) {
            return getMacroSuggestionPopup()?.handleKeyDown(event) ?? false
          },
          onExit() {
            getMacroSuggestionPopup()?.hide()
          },
        }),
      }),
    ]
  },
})
